/**
 * 這隻檔案非常地方多使用，注意 tree shaking
 */

import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Platform } from '@/env'
import { router } from '@/provide/NavProvider'

type ThemeProxyData = {
  mode: string
}

/**
 * 常用 hooks 翻譯/跳轉/語系。不要亂塞，這裡只能放只渲染一次的資訊
 */
export const useCommon = () => {
  const { t, i18n } = useTranslation()
  const theme = {
    mode: Platform.THEME,
  }

  return useMemo(() => {
    /**
     * require try catch
     * 最好使用在需要 theme 的狀態時在使用，否則用起來比沒用更麻煩
     *
     * @example 判斷 theme 時使用
     * req(({ pure, theme }) => require(`./assets/${pure}/cs.png`))
     *
     * @example 解構 default.src 時使用，但無法攔截 try-catch
     * req(require(`./assets/${pure}/cs.png`)) // 相當於 require(`./assets/${pure}/cs.png`).default.src
     */
    const req = (
      requireCallback: (theme: ThemeProxyData) => string | string,
      fallback = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgEB/wIdBLcAAAAASUVORK5CYII=' as string,
    ) => {
      const fbk = fallback
      try {
        return (typeof requireCallback === 'function' ? requireCallback?.(theme) : requireCallback) || fbk
      } catch (error) {
        console.warn('找不到圖片', error)
        return fbk
      }
    }

    /**
     * require try catch 多組 require 輪替，按照順序返回沒有拋錯的結果
     *
     * @example 場管判斷
     * reqs(
     *   // 場館
     *   () => require(`@views-common/assets/games/role/${firmCode}.png`),
     *   // 場館去掉 _S
     *   () => require(`@views-common/assets/games/role/${firmCodeReplaceS}.png`),
     *   // 預設
     *   () => require(`@views-common/assets/games/role/WAIT.png`),
     * )
     */
    const reqs = (...callbacks: Array<((theme: ThemeProxyData) => string) | string>) => {
      for (const callback of callbacks) {
        try {
          const res = typeof callback === 'function' ? callback(theme) : callback
          return res
        } catch (error) {
          // ignore
        }
      }
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgEB/wIdBLcAAAAASUVORK5CYII='
    }

    // 導頁
    const push = (path: string) => {
      router.push(path)
    }

    // 導頁並替換歷史紀錄
    const replace = (path: string) => {
      router.replace(path)
    }

    // 回上一頁
    const goBack = () => {
      router.back()
    }

    return {
      // 翻譯用
      t,
      // 當前語言
      locale: i18n.language || Platform.LOCALE,
      // 主題 Proxy
      theme,
      // 主題切圖
      req,
      reqs,
      // 導頁
      push,
      replace,
      goBack, // 回上一頁
    }
  }, [t, theme])
}
